import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { auth } from '../api/client';
import {
  User, Mail, Lock, ArrowLeft, Shield, Check,
  AlertCircle, Eye, EyeOff, KeyRound, Send,
} from 'lucide-react';
import SEO from '../components/SEO';
import { pageSeoMeta } from '@shared/pageSeoMeta';
import PhoneInput, { maxDigitsFor } from '../components/PhoneInput';
import { parseStoredPhone, type Country } from '../data/countryCodes';
import AnimatedBadge from '../components/AnimatedBadge';
import BadgeProgress from '../components/BadgeProgress';
import CoinSummaryCard from '../components/CoinSummaryCard';
import { getBadgeForOrders, nextTier } from '../lib/badges';

type PasswordStep = 'idle' | 'sending' | 'otp_sent' | 'verifying' | 'done';

export default function ProfilePage() {
  const { user, isLoading, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const profileBadge = getBadgeForOrders(user?.order_count ?? 0);
  const badgeNext = nextTier(user?.order_count ?? 0);

  // Profile edit
  const initialPhone = parseStoredPhone(user?.phone);
  const [firstName, setFirstName] = useState(user?.first_name ?? '');
  const [lastName, setLastName] = useState(user?.last_name ?? '');
  const [country, setCountry] = useState<Country>(initialPhone.country);
  const [phone, setPhone] = useState(initialPhone.nationalNumber);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileError, setProfileError] = useState('');

  // On a hard refresh the user is fetched asynchronously, so the initial
  // useState values are empty. Re-populate the form once the user arrives.
  useEffect(() => {
    if (!user) return;
    setFirstName(user.first_name ?? '');
    setLastName(user.last_name ?? '');
    const parsed = parseStoredPhone(user.phone);
    setCountry(parsed.country);
    setPhone(parsed.nationalNumber);
  }, [user]);

  // Password change
  const [passwordStep, setPasswordStep] = useState<PasswordStep>('idle');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [passMsg, setPassMsg] = useState('');
  const [passError, setPassError] = useState('');

  if (!isLoading && !user) {
    return <Navigate to="/login" replace />;
  }
  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const inp = `w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors ${
    isDark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMsg('');
    setProfileError('');
    try {
      const cleanPhone = phone.replace(/\D/g, '').slice(0, maxDigitsFor(country));
      if (cleanPhone) {
        // Validate only when a phone is entered (it's optional on the profile).
        if (country.iso === 'in') {
          if (cleanPhone.length !== 10) { setProfileError('Please enter a valid 10-digit phone number'); return; }
        } else if (!/^\+?\d{7,15}$/.test(`${country.dialCode}${cleanPhone}`)) {
          // Others: mirror the server rule (full number, incl. country code, is 7–15 digits).
          setProfileError('Please enter a valid phone number'); return;
        }
      }
      const fullPhone = cleanPhone ? `${country.dialCode}${cleanPhone}` : undefined;
      await auth.updateProfile({ first_name: firstName, last_name: lastName, phone: fullPhone });
      await refreshUser();
      setProfileMsg('Profile updated successfully');
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleRequestOTP = async () => {
    setPasswordStep('sending');
    setPassError('');
    setPassMsg('');
    try {
      await auth.requestOTP();
      setPasswordStep('otp_sent');
      setPassMsg('OTP sent to your email');
    } catch (err) {
      setPassError(err instanceof Error ? err.message : 'Failed to send OTP');
      setPasswordStep('idle');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPassError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setPassError('Password must be at least 6 characters');
      return;
    }
    setPasswordStep('verifying');
    setPassError('');
    try {
      await auth.changePassword(otp, newPassword);
      setPasswordStep('done');
      setPassMsg('Password changed successfully!');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPassError(err instanceof Error ? err.message : 'Failed to change password');
      setPasswordStep('otp_sent');
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO {...pageSeoMeta.profile} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className={`inline-flex items-center gap-2 mb-8 text-sm ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
          <ArrowLeft size={16} /> Back to Store
        </Link>

        <div className="text-center mb-8">
          {profileBadge ? (
            <>
              <div className="mx-auto mb-4" style={{ width: 96 }}>
                <AnimatedBadge src={profileBadge.src} glow={profileBadge.glow} size={96} variant="full" alt={`${profileBadge.name} badge`} />
              </div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {profileBadge.name} <span className={`font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({profileBadge.meaning})</span>
              </h1>
              <p className="mt-1 text-sm italic text-emerald-500">{profileBadge.tagline}</p>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {user!.order_count} delivered {user!.order_count === 1 ? 'order' : 'orders'}
              </p>
              <BadgeProgress count={user!.order_count} />
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <User className="text-white" size={28} />
              </div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>My Profile</h1>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {badgeNext
                  ? <>Get {badgeNext.remaining} delivered {badgeNext.remaining === 1 ? 'order' : 'orders'} to earn your first badge — <span className="font-semibold text-emerald-500">{badgeNext.tier.name}</span></>
                  : 'Manage your account settings'}
              </p>
            </>
          )}
        </div>

        {/* ZS Coins summary */}
        <div className="mb-8">
          <CoinSummaryCard />
        </div>

        {/* Profile Details Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl border p-6 mb-6 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}
        >
          <h2 className={`text-lg font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <User size={18} /> Personal Details
          </h2>

          <form onSubmit={handleProfileSave} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>First Name</label>
                <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className={inp} required />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Last Name</label>
                <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className={inp} required />
              </div>
            </div>

            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
              <div className="relative">
                <Mail size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="email"
                  value={user?.email ?? ''}
                  readOnly
                  className={`${inp} pl-10 opacity-60 cursor-not-allowed`}
                />
              </div>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Email cannot be changed</p>
            </div>

            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Phone</label>
              <PhoneInput
                country={country}
                onCountryChange={setCountry}
                value={phone}
                onChange={setPhone}
                isDark={isDark}
              />
            </div>

            {profileMsg && (
              <div className="flex items-center gap-2 bg-emerald-500/10 text-emerald-500 p-3 rounded-lg text-sm">
                <Check size={16} /> {profileMsg}
              </div>
            )}
            {profileError && (
              <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-3 rounded-lg text-sm">
                <AlertCircle size={16} /> {profileError}
              </div>
            )}

            <button
              type="submit"
              disabled={profileSaving}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors"
            >
              {profileSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </motion.div>

        {/* Change Password Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`rounded-2xl border p-6 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}
        >
          <h2 className={`text-lg font-bold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <KeyRound size={18} /> Change Password
          </h2>
          <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            We'll send a 6-digit OTP to <span className="font-medium text-emerald-500">{user?.email}</span> to verify the change.
          </p>

          {passwordStep === 'done' ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check size={28} className="text-emerald-600" />
              </div>
              <p className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Password Changed!</p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Your password has been updated successfully.</p>
              <button
                onClick={() => { setPasswordStep('idle'); setPassMsg(''); }}
                className="mt-4 text-sm text-emerald-500 hover:underline"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Step 1: Request OTP */}
              {(passwordStep === 'idle' || passwordStep === 'sending') && (
                <button
                  onClick={handleRequestOTP}
                  disabled={passwordStep === 'sending'}
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors ${
                    isDark
                      ? 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700 disabled:opacity-50'
                      : 'bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 disabled:opacity-50'
                  }`}
                >
                  {passwordStep === 'sending' ? (
                    <><div className="animate-spin w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full" /> Sending OTP...</>
                  ) : (
                    <><Send size={16} /> Send OTP to my email</>
                  )}
                </button>
              )}

              {/* Step 2: Enter OTP + New Password */}
              {(passwordStep === 'otp_sent' || passwordStep === 'verifying') && (
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enter OTP</label>
                    <div className="relative">
                      <Shield size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type="text"
                        value={otp}
                        onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit OTP"
                        maxLength={6}
                        className={`${inp} pl-10 text-center tracking-[0.3em] font-mono text-lg`}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>New Password</label>
                    <div className="relative">
                      <Lock size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type={showNewPass ? 'text' : 'password'}
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        placeholder="Min 6 characters"
                        className={`${inp} pl-10 pr-10`}
                        required
                        minLength={6}
                      />
                      <button type="button" onClick={() => setShowNewPass(!showNewPass)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {showNewPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Confirm Password</label>
                    <div className="relative">
                      <Lock size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type={showConfirmPass ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                        className={`${inp} pl-10 pr-10`}
                        required
                        minLength={6}
                      />
                      <button type="button" onClick={() => setShowConfirmPass(!showConfirmPass)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {showConfirmPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={passwordStep === 'verifying' || otp.length !== 6}
                    className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors"
                  >
                    {passwordStep === 'verifying' ? 'Verifying...' : 'Change Password'}
                  </button>

                  <button
                    type="button"
                    onClick={handleRequestOTP}
                    className={`w-full text-center text-xs ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    Didn't receive OTP? Resend
                  </button>
                </form>
              )}

              {passMsg && (
                <div className="flex items-center gap-2 bg-emerald-500/10 text-emerald-500 p-3 rounded-lg text-sm mt-4">
                  <Check size={16} /> {passMsg}
                </div>
              )}
              {passError && (
                <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-3 rounded-lg text-sm mt-4">
                  <AlertCircle size={16} /> {passError}
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
